import { Button, IconButton, SelectInput, SelectInputItem } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Textarea } from '@/shared/ui/atoms';
import { useStatusReasons } from '../../../hooks/useStatusReasons';
import { useSubStatusReasons } from '../../../hooks/useSubStatusReasons';
import type { EnrollmentModalData, EnrollmentStatus } from '../../../types/enrollment';
import { EnrollmentStatusBadge } from '../../atoms/EnrollmentStatusBadge';

const IN_REGISTRATION_STATUSES: readonly EnrollmentStatus[] = [
  'payment_selection',
  'payment_pending',
  'approval_pending',
];

function getCancelStatusCode(status: EnrollmentStatus): 9 | 10 {
  return IN_REGISTRATION_STATUSES.includes(status) ? 10 : 9;
}

export interface CancelEnrollmentModalProps {
  readonly enrollment: EnrollmentModalData | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (data: {
    enrollmentId: number;
    reasonId: string;
    subReasonId: string;
    observation: string;
  }) => void;
}

export function CancelEnrollmentModal({
  enrollment,
  open,
  onClose,
  onConfirm,
}: CancelEnrollmentModalProps) {
  const { t } = useTranslation('personManagement');

  const [reasonId, setReasonId] = useState('');
  const [subReasonId, setSubReasonId] = useState('');
  const [observation, setObservation] = useState('');
  const [errors, setErrors] = useState({
    reasonId: false,
    subReasonId: false,
    observation: false,
  });

  useEffect(() => {
    if (open) return;
    const id = setTimeout(() => {
      document.body.style.removeProperty('pointer-events');
    }, 0);
    return () => clearTimeout(id);
  }, [open]);

  const cancelStatus = enrollment ? getCancelStatusCode(enrollment.status) : undefined;
  const { reasons } = useStatusReasons(cancelStatus);
  const { subReasons } = useSubStatusReasons(reasonId ? Number(reasonId) : undefined);
  const hasSubReasons = subReasons.length > 0;

  const resetForm = () => {
    setReasonId('');
    setSubReasonId('');
    setObservation('');
    setErrors({ reasonId: false, subReasonId: false, observation: false });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleConfirm = () => {
    if (!enrollment) return;

    const reasonErr = !reasonId;
    const subReasonErr = !!(hasSubReasons && !subReasonId);
    const observationErr = !observation.trim();

    if (reasonErr || subReasonErr || observationErr) {
      setErrors({
        reasonId: reasonErr,
        subReasonId: subReasonErr,
        observation: observationErr,
      });
      return;
    }

    onConfirm({
      enrollmentId: enrollment.id,
      reasonId,
      subReasonId,
      observation: observation.trim(),
    });

    resetForm();
  };

  if (!enrollment) return null;

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft ![animation:none]" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none ![animation:none]"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-xs py-mini">
            <Dialog.Title className="ts-heading-sm font-semibold text-ctx-content-title">
              {t('cancelModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.cancel')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-xxs overflow-y-auto px-xs py-mini">
            {/* Enrollment summary */}
            <div className="flex flex-col gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
              <div className="flex flex-wrap items-center gap-xs">
                <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                  {enrollment.personName}
                </span>
                <EnrollmentStatusBadge status={enrollment.status} />
              </div>
              <div className="grid grid-cols-2 gap-xs">
                <div className="flex flex-col gap-quark">
                  <span className="ts-subtitle-xxs text-ctx-content-base">
                    {t('table.columns.course')}
                  </span>
                  <span className="ts-caption-xxs text-ctx-content-placeholder">
                    {enrollment.courseName}
                  </span>
                </div>
                <div className="flex flex-col gap-quark">
                  <span className="ts-subtitle-xxs text-ctx-content-base">
                    {t('table.columns.className')}
                  </span>
                  <span className="ts-caption-xxs text-ctx-content-placeholder">
                    {enrollment.className}
                  </span>
                </div>
              </div>
            </div>

            <p className="ts-paragraph-xs text-ctx-content-title">
              {t('cancelModal.confirmationMessage')}
            </p>

            {/* Reason */}
            <div className="flex flex-col gap-quark">
              <SelectInput
                label={t('cancelModal.reason')}
                value={reasonId || 'none'}
                custom={{ content: 'z-[9999]' }}
                onValueChange={v => {
                  setReasonId(v === 'none' ? '' : v);
                  setSubReasonId('');
                  setErrors(p => ({ ...p, reasonId: false }));
                }}
              >
                <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
                  {t('cancelModal.selectReason')}
                </SelectInputItem>
                {reasons.map(r => (
                  <SelectInputItem key={r.id} value={r.id} custom={{ root: 'text-nowrap' }}>
                    {r.name}
                  </SelectInputItem>
                ))}
              </SelectInput>
              {errors.reasonId && (
                <span className="ts-caption-xxs text-negative-500">
                  {t('cancelModal.reasonRequired')}
                </span>
              )}
            </div>

            {/* Sub-reason */}
            {hasSubReasons && (
              <div className="flex flex-col gap-quark">
                <SelectInput
                  label={t('cancelModal.subReason')}
                  value={subReasonId || 'none'}
                  onValueChange={v => {
                    setSubReasonId(v === 'none' ? '' : v);
                    setErrors(p => ({ ...p, subReasonId: false }));
                  }}
                >
                  <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
                    {t('cancelModal.selectSubReason')}
                  </SelectInputItem>
                  {subReasons.map(sr => (
                    <SelectInputItem key={sr.id} value={sr.id} custom={{ root: 'text-nowrap' }}>
                      {sr.name}
                    </SelectInputItem>
                  ))}
                </SelectInput>
                {errors.subReasonId && (
                  <span className="ts-caption-xxs text-negative-500">
                    {t('cancelModal.subReasonRequired')}
                  </span>
                )}
              </div>
            )}

            {/* Observation */}
            <div className="flex flex-col gap-quark">
              <label htmlFor="cancel-observation" className="ts-caption-xxs text-ctx-content-base">
                {t('cancelModal.observation')}
              </label>
              <Textarea
                id="cancel-observation"
                placeholder={t('cancelModal.observationPlaceholder')}
                value={observation}
                onChange={e => {
                  setObservation(e.target.value);
                  setErrors(p => ({ ...p, observation: false }));
                }}
                rows={3}
              />
              {errors.observation && (
                <span className="ts-caption-xxs text-negative-500">
                  {t('cancelModal.observationRequired')}
                </span>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border px-md py-xs">
            <Button hierarchy="secondary" size="lg" onClick={handleClose}>
              {t('cancelModal.no')}
            </Button>
            <Button size="lg" hierarchy="danger" onClick={handleConfirm}>
              {t('cancelModal.yes')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
